import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import VideoCard from "@/components/VideoCard";
import SEO from "@/components/SEO";
import AdSlot from "@/components/AdSlot";
import PageHero from "@/components/PageHero";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Loader2, X } from "lucide-react";

export default function Archive() {
  const [params, setParams] = useSearchParams();
  const [speeches, setSpeeches] = useState([]);
  const [meta, setMeta] = useState({ leaders: [], countries: [], themes: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");
  const [liveMsg, setLiveMsg] = useState("");
  const justCleared = useRef(false);

  const activeLeader = params.get("leader") || "";
  const activeCountry = params.get("country") || "";
  const activeTheme = params.get("theme") || "";

  useEffect(() => {
    api.get("/speeches/meta/filters").then((r) => setMeta(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = {};
    if (params.get("search")) q.search = params.get("search");
    if (activeLeader) q.leader = activeLeader;
    if (activeCountry) q.country = activeCountry;
    if (activeTheme) q.theme = activeTheme;
    api.get("/speeches", { params: q }).then((r) => {
      setSpeeches(r.data);
      const n = r.data.length;
      const base = n === 0
        ? "Nessun discorso corrisponde ai criteri selezionati"
        : `${n} discors${n === 1 ? "o" : "i"} trovat${n === 1 ? "o" : "i"}`;
      setLiveMsg(justCleared.current ? `Filtri rimossi. ${base}` : base);
      justCleared.current = false;
    }).finally(() => setLoading(false));
  }, [params, activeLeader, activeCountry, activeTheme]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value && next.get(key) !== value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const doSearch = (e) => {
    e.preventDefault();
    setFilter("search", search);
  };

  const clearAll = () => {
    justCleared.current = true;
    setSearch("");
    setParams(new URLSearchParams());
  };

  const hasFilters = activeLeader || activeCountry || activeTheme || params.get("search");

  const FilterGroup = ({ title, items, keyName, active }) => (
    <div className="mb-6">
      <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-3">{title}</h4>
      <div className="flex flex-col gap-1">
        {items.map((it) => (
          <button
            key={it}
            data-testid={`filter-${keyName}-${it}`}
            onClick={() => setFilter(keyName, it)}
            className={`text-left text-sm py-1 px-2 rounded-sm transition-colors ${
              active === it ? "bg-primary text-primary-foreground" : "hover:bg-muted text-foreground"
            }`}
          >
            {it}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div>
      <SEO
        title="Archivio dei discorsi ufficiali"
        description="Cerca discorsi, dichiarazioni e conferenze stampa per leader, Paese o argomento. Guarda il video e confronta il testo originale con la traduzione italiana."
        canonicalPath="/archivio"
        noindex={Boolean(hasFilters)}
      />
      <PageHero
        crumb="Archivio"
        overline="Tutti i discorsi"
        title="L'archivio dei discorsi ufficiali"
        subtitle="Cerca per leader, Paese o argomento. Guarda il video e confronta il testo originale con la traduzione italiana. La fonte ufficiale è sempre indicata."
        icon={Search}
      />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <form onSubmit={doSearch} className="flex gap-2 mb-8 max-w-xl">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="archive-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca un leader, un Paese o un argomento…"
            className="pl-9 rounded-sm h-11"
          />
        </div>
        <Button type="submit" className="rounded-sm h-11" data-testid="archive-search-btn">Cerca</Button>
      </form>

      <div className="grid lg:grid-cols-[220px_1fr] gap-10">
        <aside>
          <h3 className="text-sm font-semibold mb-4">Filtra per</h3>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearAll} className="mb-4 gap-1 text-accent" data-testid="clear-filters-btn">
              <X className="h-3 w-3" /> Rimuovi tutti i filtri
            </Button>
          )}
          {meta.themes.length > 0 && <FilterGroup title="Argomenti" items={meta.themes} keyName="theme" active={activeTheme} />}
          {meta.countries.length > 0 && <FilterGroup title="Paesi" items={meta.countries} keyName="country" active={activeCountry} />}
          {meta.leaders.length > 0 && <FilterGroup title="Leader" items={meta.leaders} keyName="leader" active={activeLeader} />}
        </aside>

        <div>
          <div
            role="status"
            aria-live="polite"
            data-testid="archive-results-status"
            className="text-sm font-medium text-foreground mb-4 min-h-[1.25rem]"
          >
            {!loading && liveMsg}
          </div>
          <AdSlot placement="archive_top" className="mb-6" />
          {loading ? (
            <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : speeches.length === 0 ? (
            <div className="py-10">
              <p className="text-muted-foreground mb-4">Nessun discorso corrisponde ai criteri selezionati.</p>
              {hasFilters && (
                <Button variant="outline" onClick={clearAll} className="rounded-sm" data-testid="clear-filters-empty-btn">
                  Rimuovi i filtri e mostra tutto
                </Button>
              )}
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {speeches.map((s) => <VideoCard key={s.id} speech={s} />)}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
